/**
 * The Asset Management contract — register, categories, custody lifecycle, and employee requests.
 *
 * Follows monorepo conventions: ts-rest contract with routeMeta on every route, DTO schemas
 * carrying authorization fields, zod validation, and cursor-based pagination.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';
import { assetCustomFieldValuesSchema } from './asset-custom-fields.contract.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

// ---------------------------------------------------------------------------
// Primitives & Enums
// ---------------------------------------------------------------------------

export const assetConditionSchema = z.enum(['new', 'good', 'fair', 'poor', 'damaged']);
export type AssetCondition = z.infer<typeof assetConditionSchema>;

export const assetStatusSchema = z.enum([
  'available',
  'assigned',
  'maintenance',
  'damaged',
  'lost',
  'retired',
  'disposed',
]);
export type AssetStatus = z.infer<typeof assetStatusSchema>;

/**
 * Lifecycle actions that take an asset out of (or partway out of) service (AST-7). `status` only
 * changes through these, through custody (assign / return / allocate) and through maintenance
 * tickets — never through a plain edit.
 */
export const assetLifecycleActionSchema = z.enum([
  'retire',
  'dispose',
  'mark_lost',
  'mark_damaged',
]);
export type AssetLifecycleAction = z.infer<typeof assetLifecycleActionSchema>;

/** The status each lifecycle action leaves the asset in. */
export const ASSET_LIFECYCLE_TARGET_STATUS: Readonly<Record<AssetLifecycleAction, AssetStatus>> = {
  retire: 'retired',
  dispose: 'disposed',
  mark_lost: 'lost',
  mark_damaged: 'damaged',
};

/**
 * The statuses each action may start from. An assigned asset must be returned before it can be
 * retired, disposed or marked damaged; a lost asset may still be written off (disposed).
 */
export const ASSET_LIFECYCLE_ALLOWED_FROM: Readonly<
  Record<AssetLifecycleAction, readonly AssetStatus[]>
> = {
  retire: ['available', 'damaged'],
  dispose: ['retired', 'lost'],
  mark_lost: ['available', 'assigned', 'maintenance', 'damaged'],
  mark_damaged: ['available', 'maintenance'],
};

export function canApplyAssetLifecycleAction(
  status: AssetStatus,
  action: AssetLifecycleAction,
): boolean {
  return ASSET_LIFECYCLE_ALLOWED_FROM[action].includes(status);
}

/** Statuses in which an asset can no longer be allocated or sent for repair. */
export const ASSET_OUT_OF_SERVICE_STATUSES: readonly AssetStatus[] = [
  'retired',
  'disposed',
  'lost',
];

export const assetDisposalMethodSchema = z.enum([
  'sale',
  'scrap',
  'recycle',
  'donation',
  'write_off',
  'return_to_vendor',
]);
export type AssetDisposalMethod = z.infer<typeof assetDisposalMethodSchema>;

export const assetOriginSchema = z.enum(['requested', 'direct', 'transfer']);
export type AssetOrigin = z.infer<typeof assetOriginSchema>;

export const custodyTypeSchema = z.enum(['primary', 'secondary', 'loaner', 'departmental']);
export type CustodyType = z.infer<typeof custodyTypeSchema>;

export const assetRequestTypeSchema = z.enum(['new', 'replacement']);
export type AssetRequestType = z.infer<typeof assetRequestTypeSchema>;

export const assetRequestStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
  'cancelled',
  'fulfilled',
]);
export type AssetRequestStatus = z.infer<typeof assetRequestStatusSchema>;

export const assetHistoryEventTypeSchema = z.enum([
  'created',
  'assigned',
  'acknowledged',
  'transferred',
  'return_requested',
  'returned',
  'maintenance_created',
  'maintenance_completed',
  'maintenance_cancelled',
  'updated',
  'condition_changed',
  'retired',
  'disposed',
  'marked_lost',
  'marked_damaged',
]);
export type AssetHistoryEventType = z.infer<typeof assetHistoryEventTypeSchema>;

export const assetSpecItemSchema = z.object({
  label: z.string().trim().min(1),
  value: z.string().trim().min(1),
});
export type AssetSpecItem = z.infer<typeof assetSpecItemSchema>;

export const assetSummaryPersonSchema = z.object({
  id: uuidSchema,
  displayName: z.string(),
});
export type AssetSummaryPerson = z.infer<typeof assetSummaryPersonSchema>;

// ---------------------------------------------------------------------------
// Category & Type Schemas
// ---------------------------------------------------------------------------

export const assetCategorySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  isActive: z.boolean(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type AssetCategory = z.infer<typeof assetCategorySchema>;

export const createAssetCategoryBodySchema = z.object({
  code: z.string().trim().min(1).max(50),
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).optional(),
});
export type CreateAssetCategoryBody = z.infer<typeof createAssetCategoryBodySchema>;

export const assetTypeSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  categoryId: uuidSchema,
  categoryName: z.string().optional(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  isActive: z.boolean(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type AssetType = z.infer<typeof assetTypeSchema>;

export const createAssetTypeBodySchema = z.object({
  categoryId: uuidSchema,
  code: z.string().trim().min(1).max(50),
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).optional(),
});
export type CreateAssetTypeBody = z.infer<typeof createAssetTypeBodySchema>;

// ---------------------------------------------------------------------------
// Assignment & History Schemas
// ---------------------------------------------------------------------------

export const assetAssignmentSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  assetId: uuidSchema,
  assetCode: z.string().optional(),
  assetName: z.string().optional(),
  personId: uuidSchema,
  custodian: assetSummaryPersonSchema.optional(),
  assignedAt: instantSchema,
  endedAt: instantSchema.nullable(),
  origin: assetOriginSchema,
  custodyType: custodyTypeSchema.optional(),
  assetRequestId: uuidSchema.nullable(),
  assignedById: uuidSchema,
  assignedBy: assetSummaryPersonSchema.optional(),
  conditionAtAssign: assetConditionSchema,
  acknowledgedAt: instantSchema.nullable(),
  exceptionNotes: z.string().nullable().optional(),
  manifestChecksum: z.string().nullable().optional(),
  returnRequestedAt: instantSchema.nullable(),
  returnRequestNote: z.string().nullable().optional(),
  returnedAt: instantSchema.nullable().optional(),
  returnedToId: uuidSchema.nullable().optional(),
  returnedTo: assetSummaryPersonSchema.nullable().optional(),
  conditionAtReturn: assetConditionSchema.nullable().optional(),
  returnNote: z.string().nullable().optional(),
  createdAt: instantSchema,
});
export type AssetAssignment = z.infer<typeof assetAssignmentSchema>;

export const assetHistoryEventSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  assetId: uuidSchema,
  eventType: assetHistoryEventTypeSchema,
  custodyType: custodyTypeSchema.nullable().optional(),
  actorPersonId: uuidSchema.nullable(),
  actor: assetSummaryPersonSchema.nullable().optional(),
  fromPersonId: uuidSchema.nullable().optional(),
  fromPerson: assetSummaryPersonSchema.nullable().optional(),
  toPersonId: uuidSchema.nullable().optional(),
  toPerson: assetSummaryPersonSchema.nullable().optional(),
  conditionBefore: assetConditionSchema.nullable().optional(),
  conditionAfter: assetConditionSchema.nullable().optional(),
  assetRequestId: uuidSchema.nullable().optional(),
  note: z.string().nullable().optional(),
  createdAt: instantSchema,
});
export type AssetHistoryEvent = z.infer<typeof assetHistoryEventSchema>;

// ---------------------------------------------------------------------------
// Asset Schemas
// ---------------------------------------------------------------------------

export const assetDisposalSchema = z.object({
  method: assetDisposalMethodSchema,
  disposedOn: dateOnlySchema,
  /** Only returned to callers with `asset.financial:read`. */
  valueReceived: decimalStringSchema.nullable().optional(),
  currency: currencySchema.nullable().optional(),
  note: z.string().nullable().optional(),
});
export type AssetDisposal = z.infer<typeof assetDisposalSchema>;

export const assetSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  assetCode: z.string(),
  categoryId: uuidSchema,
  categoryName: z.string().optional(),
  typeId: uuidSchema.nullable(),
  typeName: z.string().nullable().optional(),
  name: z.string(),
  serialNumber: z.string().nullable(),
  locationId: uuidSchema.nullable(),
  locationName: z.string().nullable().optional(),
  purchaseDate: dateOnlySchema.nullable(),
  warrantyExpiresOn: dateOnlySchema.nullable(),
  specs: z.array(assetSpecItemSchema).nullable().optional(),
  /** Category custom-field values keyed by field id (AST-12); only fields with a value appear. */
  customFields: assetCustomFieldValuesSchema.optional(),
  purchaseValue: decimalStringSchema.optional(),
  purchaseCurrency: currencySchema.optional(),
  status: assetStatusSchema,
  condition: assetConditionSchema,
  currentAssignment: assetAssignmentSchema.nullable().optional(),
  /** When the asset was taken out of service (retired, or marked lost). */
  retiredAt: instantSchema.nullable().optional(),
  /** Set once the asset is disposed. */
  disposal: assetDisposalSchema.nullable().optional(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type Asset =z.infer<typeof assetSchema>;

export const createAssetBodySchema = z.object({
  categoryId: uuidSchema,
  /** Required, and must belong to `categoryId` (AST-18). */
  typeId: uuidSchema,
  name: z.string().trim().min(1).max(200),
  serialNumber: z.string().trim().max(100).optional(),
  locationId: uuidSchema.optional(),
  purchaseDate: dateOnlySchema.optional(),
  warrantyExpiresOn: dateOnlySchema.optional(),
  specs: z.array(assetSpecItemSchema).optional(),
  /** Validated server-side against the category's active custom-field definitions (AST-12). */
  customFields: assetCustomFieldValuesSchema.optional(),
  purchaseValue: decimalStringSchema.optional(),
  purchaseCurrency: currencySchema.optional(),
  condition: assetConditionSchema.optional().default('new'),
});
export type CreateAssetBody = z.infer<typeof createAssetBodySchema>;

export const patchAssetBodySchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    serialNumber: z.string().trim().max(100).nullable().optional(),
    locationId: uuidSchema.nullable().optional(),
    purchaseDate: dateOnlySchema.nullable().optional(),
    warrantyExpiresOn: dateOnlySchema.nullable().optional(),
    specs: z.array(assetSpecItemSchema).nullable().optional(),
    /** Fields present are set (null clears); absent fields are untouched (AST-12). */
    customFields: assetCustomFieldValuesSchema.optional(),
    purchaseValue: decimalStringSchema.nullable().optional(),
    purchaseCurrency: currencySchema.nullable().optional(),
    /** Condition may change, with history; `status` is deliberately not editable here (AST-19). */
    condition: assetConditionSchema.optional(),
  })
  .strict();
export type PatchAssetBody = z.infer<typeof patchAssetBodySchema>;

export const assignAssetBodySchema = z.object({
  personId: uuidSchema,
  condition: assetConditionSchema.optional().default('good'),
  note: z.string().trim().max(500).optional(),
});
export type AssignAssetBody = z.infer<typeof assignAssetBodySchema>;

export const transferAssetBodySchema = z.object({
  toPersonId: uuidSchema,
  condition: assetConditionSchema.optional().default('good'),
  note: z.string().trim().max(500).optional(),
});
export type TransferAssetBody = z.infer<typeof transferAssetBodySchema>;

export const returnAssetBodySchema = z.object({
  condition: assetConditionSchema,
  note: z.string().trim().max(500).optional(),
});
export type ReturnAssetBody = z.infer<typeof returnAssetBodySchema>;

export const retireAssetBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type RetireAssetBody = z.infer<typeof retireAssetBodySchema>;

export const markAssetLostBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type MarkAssetLostBody = z.infer<typeof markAssetLostBodySchema>;

export const markAssetDamagedBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type MarkAssetDamagedBody = z.infer<typeof markAssetDamagedBodySchema>;

export const disposeAssetBodySchema = z
  .object({
    method: assetDisposalMethodSchema,
    disposedOn: dateOnlySchema,
    /** What the organisation received (sale price, scrap value). Omit for nil-value disposals. */
    valueReceived: decimalStringSchema
      .refine((v) => !v.startsWith('-'), 'Must not be negative')
      .optional(),
    /** Required whenever `valueReceived` is given. */
    currency: currencySchema.optional(),
    note: z.string().trim().max(500).optional(),
  })
  .superRefine((body, ctx) => {
    if (body.valueReceived !== undefined && body.currency === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['currency'],
        message: 'currency is required with valueReceived',
      });
    }
  });
export type DisposeAssetBody = z.infer<typeof disposeAssetBodySchema>;

// ---------------------------------------------------------------------------
// Request Schemas
// ---------------------------------------------------------------------------

export const assetRequestSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  requestCode: z.string(),
  personId: uuidSchema,
  requester: assetSummaryPersonSchema.optional(),
  filedByPersonId: uuidSchema.nullable(),
  filedBy: assetSummaryPersonSchema.nullable().optional(),
  requestType: assetRequestTypeSchema,
  replacesAssetId: uuidSchema.nullable(),
  replacesAssetCode: z.string().nullable().optional(),
  replacesAssetName: z.string().nullable().optional(),
  categoryId: uuidSchema,
  categoryName: z.string().optional(),
  typeId: uuidSchema.nullable(),
  typeName: z.string().nullable().optional(),
  justification: z.string(),
  assetId: uuidSchema.nullable(),
  assetCode: z.string().nullable().optional(),
  assetName: z.string().nullable().optional(),
  status: assetRequestStatusSchema,
  workflowInstanceId: uuidSchema.nullable(),
  currentStepNo: z.number().int().nullable().optional(),
  totalSteps: z.number().int().nullable().optional(),
  currentApproverName: z.string().nullable().optional(),
  dueAt: instantSchema.nullable().optional(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type AssetRequest = z.infer<typeof assetRequestSchema>;

export const createAssetRequestBodySchema = z.object({
  personId: uuidSchema.optional(), // When filing on behalf of another person
  requestType: assetRequestTypeSchema.optional().default('new'),
  replacesAssetId: uuidSchema.optional(),
  categoryId: uuidSchema,
  typeId: uuidSchema.optional(),
  justification: z.string().trim().min(1).max(1000),
});
export type CreateAssetRequestBody = z.infer<typeof createAssetRequestBodySchema>;

export const decideAssetRequestBodySchema = z.object({
  note: z.string().trim().max(500).optional(),
});
export type DecideAssetRequestBody = z.infer<typeof decideAssetRequestBodySchema>;

export const rejectAssetRequestBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type RejectAssetRequestBody = z.infer<typeof rejectAssetRequestBodySchema>;

export const allocateAssetRequestBodySchema = z.object({
  assetId: uuidSchema,
  condition: assetConditionSchema.optional().default('good'),
});
export type AllocateAssetRequestBody = z.infer<typeof allocateAssetRequestBodySchema>;

export const returnRequestBodySchema = z.object({
  note: z.string().trim().max(500).optional(),
});
export type ReturnRequestBody = z.infer<typeof returnRequestBodySchema>;

// ---------------------------------------------------------------------------
// Maintenance Schemas
// ---------------------------------------------------------------------------

export const maintenanceIssuePrioritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export type MaintenanceIssuePriority = z.infer<typeof maintenanceIssuePrioritySchema>;

export const maintenanceIssueStatusSchema = z.enum([
  'reported',
  'open_triage',
  'staged_for_repair',
  'parts_vendor',
  'qa_verified',
  'closed_resolved',
  'cancelled',
]);
export type MaintenanceIssueStatus = z.infer<typeof maintenanceIssueStatusSchema>;

/**
 * The moves a maintenance ticket may make (AST-34). `closed_resolved` and `cancelled` are
 * terminal. Re-sending the current status is a no-op, not a transition.
 */
export const MAINTENANCE_STATUS_TRANSITIONS: Readonly<
  Record<MaintenanceIssueStatus, readonly MaintenanceIssueStatus[]>
> = {
  reported: ['open_triage', 'cancelled'],
  open_triage: ['staged_for_repair', 'parts_vendor', 'closed_resolved', 'cancelled'],
  staged_for_repair: ['parts_vendor', 'qa_verified', 'closed_resolved', 'cancelled'],
  parts_vendor: ['staged_for_repair', 'qa_verified', 'closed_resolved', 'cancelled'],
  qa_verified: ['staged_for_repair', 'closed_resolved', 'cancelled'],
  closed_resolved: [],
  cancelled: [],
};

/** Tickets in these statuses no longer hold the asset in `maintenance`. */
export const MAINTENANCE_CLOSED_STATUSES: readonly MaintenanceIssueStatus[] = [
  'closed_resolved',
  'cancelled',
];

export function canTransitionMaintenanceStatus(
  from: MaintenanceIssueStatus,
  to: MaintenanceIssueStatus,
): boolean {
  return from === to || MAINTENANCE_STATUS_TRANSITIONS[from].includes(to);
}

export const maintenanceCommentSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  issueId: uuidSchema,
  authorId: uuidSchema,
  author: assetSummaryPersonSchema.optional(),
  isInternalOnly: z.boolean(),
  message: z.string(),
  attachmentUrls: z.array(z.string()).nullable().optional(),
  createdAt: instantSchema,
});
export type MaintenanceComment = z.infer<typeof maintenanceCommentSchema>;

export const createMaintenanceCommentBodySchema = z.object({
  message: z.string().trim().min(1).max(2000),
  isInternalOnly: z.boolean().optional().default(false),
  attachmentUrls: z.array(z.string().url()).optional(),
});
export type CreateMaintenanceCommentBody = z.infer<typeof createMaintenanceCommentBodySchema>;

export const maintenanceIssueSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  issueCode: z.string(),
  assetId: uuidSchema,
  assetName: z.string().optional(),
  assetCode: z.string().optional(),
  loanerAssetId: uuidSchema.nullable().optional(),
  loanerAssetCode: z.string().nullable().optional(),
  reporterId: uuidSchema,
  reporter: assetSummaryPersonSchema.optional(),
  technicianId: uuidSchema.nullable().optional(),
  technician: assetSummaryPersonSchema.nullable().optional(),
  vendorId: z.string().nullable().optional(),
  vendorTicketRef: z.string().nullable().optional(),
  issueType: z.string(),
  title: z.string(),
  description: z.string(),
  diagnosticsNote: z.string().nullable().optional(),
  priority: maintenanceIssuePrioritySchema,
  status: maintenanceIssueStatusSchema,
  reportedAt: instantSchema,
  slaDueAt: instantSchema.nullable().optional(),
  diagnosedAt: instantSchema.nullable().optional(),
  repairStartedAt: instantSchema.nullable().optional(),
  completedAt: instantSchema.nullable().optional(),
  verifiedAt: instantSchema.nullable().optional(),
  partsUsed: z.array(z.string()).nullable().optional(),
  repairCost: decimalStringSchema.nullable().optional(),
  repairCurrency: currencySchema.nullable().optional(),
  isWarrantyClaim: z.boolean().optional(),
  downtimeHours: z.number().nullable().optional(),
  resolutionSummary: z.string().nullable().optional(),
  attachmentUrls: z.array(z.string()).nullable().optional(),
  comments: z.array(maintenanceCommentSchema).optional(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type MaintenanceIssue = z.infer<typeof maintenanceIssueSchema>;

export const createMaintenanceIssueBodySchema = z.object({
  assetId: uuidSchema,
  issueType: z.string().trim().min(1).max(50),
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().min(1).max(2000),
  priority: maintenanceIssuePrioritySchema.optional().default('medium'),
  technicianId: uuidSchema.optional(),
  attachmentUrls: z.array(z.string().url()).optional(),
});
export type CreateMaintenanceIssueBody = z.infer<typeof createMaintenanceIssueBodySchema>;

export const patchMaintenanceIssueBodySchema = z.object({
  status: maintenanceIssueStatusSchema.optional(),
  diagnosticsNote: z.string().trim().max(1000).optional(),
  technicianId: uuidSchema.nullable().optional(),
  /** A vendor record id (AST-26) — validated against the org's vendors; free text is no longer accepted. */
  vendorId: uuidSchema.nullable().optional(),
  vendorTicketRef: z.string().trim().max(100).nullable().optional(),
  partsUsed: z.array(z.string().trim().min(1)).optional(),
  repairCost: decimalStringSchema.optional(),
  repairCurrency: currencySchema.optional(),
  isWarrantyClaim: z.boolean().optional(),
  downtimeHours: z.number().nonnegative().optional(),
  resolutionSummary: z.string().trim().max(1000).optional(),
});
export type PatchMaintenanceIssueBody = z.infer<typeof patchMaintenanceIssueBodySchema>;

export const assignTechnicianBodySchema = z.object({
  technicianId: uuidSchema.nullable(),
  slaHours: z.number().int().positive().optional(),
});
export type AssignTechnicianBody = z.infer<typeof assignTechnicianBodySchema>;

export const issueLoanerAssetBodySchema = z.object({
  loanerAssetId: uuidSchema,
  note: z.string().trim().max(500).optional(),
});
export type IssueLoanerAssetBody = z.infer<typeof issueLoanerAssetBodySchema>;

export const acknowledgeAssignmentWithExceptionBodySchema = z.object({
  exceptionNotes: z.string().trim().max(1000).optional(),
  manifestChecksum: z.string().trim().max(128).optional(),
});
export type AcknowledgeAssignmentWithExceptionBody = z.infer<
  typeof acknowledgeAssignmentWithExceptionBodySchema
>;

// ---------------------------------------------------------------------------
// Dashboard Schemas
// ---------------------------------------------------------------------------

export const assetDashboardStatusItemSchema = z.object({
  status: z.string(),
  label: z.string(),
  count: z.number(),
  percentage: z.number(),
});
export type AssetDashboardStatusItem = z.infer<typeof assetDashboardStatusItemSchema>;

export const assetDashboardDepartmentItemSchema = z.object({
  departmentId: z.string().nullable(),
  departmentName: z.string(),
  count: z.number(),
  percentage: z.number(),
});
export type AssetDashboardDepartmentItem = z.infer<typeof assetDashboardDepartmentItemSchema>;

export const assetDashboardLocationItemSchema = z.object({
  locationId: z.string().nullable(),
  locationName: z.string(),
  count: z.number(),
  percentage: z.number(),
});
export type AssetDashboardLocationItem = z.infer<typeof assetDashboardLocationItemSchema>;

export const assetDashboardCategoryItemSchema = z.object({
  categoryId: z.string(),
  categoryName: z.string(),
  categoryCode: z.string(),
  total: z.number(),
  assigned: z.number(),
  available: z.number(),
  maintenance: z.number(),
  utilizationPercentage: z.number(),
});
export type AssetDashboardCategoryItem = z.infer<typeof assetDashboardCategoryItemSchema>;

export const assetDashboardAlertItemSchema = z.object({
  count: z.number(),
  label: z.string(),
  description: z.string(),
  assetCodes: z.array(z.string()).optional(),
});
export type AssetDashboardAlertItem = z.infer<typeof assetDashboardAlertItemSchema>;

export const assetDashboardAlertsSchema = z.object({
  overdueReturns: assetDashboardAlertItemSchema,
  /** Assets whose status is `lost` or `damaged`. */
  lostOrDamaged: assetDashboardAlertItemSchema,
  /** Assets recorded in `poor` or `damaged` condition — a condition figure, not a status. */
  poorCondition: assetDashboardAlertItemSchema.optional(),
  warrantyExpiring30Days: assetDashboardAlertItemSchema,
  maintenanceOverdue: assetDashboardAlertItemSchema,
  pendingAcknowledgment: assetDashboardAlertItemSchema,
});
export type AssetDashboardAlerts = z.infer<typeof assetDashboardAlertsSchema>;

export const assetDashboardRequestPipelineSchema = z.object({
  totalActive: z.number(),
  newRequests: z.number(),
  pendingApproval: z.number(),
  approved: z.number(),
  awaitingAllocation: z.number(),
  inTransitOrHandover: z.number(),
});
export type AssetDashboardRequestPipeline = z.infer<typeof assetDashboardRequestPipelineSchema>;

export const assetDashboardMaintenanceWorkloadSchema = z.object({
  openIssues: z.number(),
  criticalCount: z.number(),
  highCount: z.number(),
  mediumCount: z.number(),
  lowCount: z.number(),
  inProgressCount: z.number(),
  scheduledCount: z.number(),
  overdueCount: z.number(),
  restoredThisMonth: z.number(),
  mttrDays: z.number().nullable(),
  warrantySuccessRate: z.number().nullable(),
  breakdown: z.array(
    z.object({
      name: z.string(),
      count: z.number(),
      detail: z.string(),
    }),
  ),
});
export type AssetDashboardMaintenanceWorkload = z.infer<
  typeof assetDashboardMaintenanceWorkloadSchema
>;

export const assetDashboardRecentActivityItemSchema = z.object({
  id: z.string(),
  action: z.string(),
  actionLabel: z.string(),
  assetCode: z.string(),
  assetName: z.string(),
  performedBy: z.string(),
  details: z.string(),
  timestamp: instantSchema,
  badgeTone: z.enum(['blue', 'emerald', 'amber', 'purple', 'slate', 'rose']),
});
export type AssetDashboardRecentActivityItem = z.infer<
  typeof assetDashboardRecentActivityItemSchema
>;

export const assetDashboardFinancialSummarySchema = z.object({
  totalPurchaseValue: z.string(),
  currency: z.string(),
});
export type AssetDashboardFinancialSummary = z.infer<typeof assetDashboardFinancialSummarySchema>;

export const assetDashboardScopeInfoSchema = z.object({
  scopeType: z.enum(['org', 'reporting_tree', 'department', 'location', 'self']),
  scopeLabel: z.string(),
  organizationName: z.string(),
  orgId: z.string(),
});
export type AssetDashboardScopeInfo = z.infer<typeof assetDashboardScopeInfoSchema>;

export const assetDashboardSchema = z.object({
  // KPIs
  totalAssets: z.number(),
  assignedAssets: z.number(),
  availableAssets: z.number(),
  maintenanceAssets: z.number(),
  pendingActionsCount: z.number(),
  operationalAvailabilityPercentage: z.number(),

  // Distributions
  statusBreakdown: z.array(assetDashboardStatusItemSchema),
  departmentBreakdown: z.array(assetDashboardDepartmentItemSchema),
  locationBreakdown: z.array(assetDashboardLocationItemSchema),
  categoryBreakdown: z.array(assetDashboardCategoryItemSchema),

  // Operational Modules
  alerts: assetDashboardAlertsSchema,
  requestPipeline: assetDashboardRequestPipelineSchema,
  maintenanceWorkload: assetDashboardMaintenanceWorkloadSchema,
  recentActivity: z.array(assetDashboardRecentActivityItemSchema),

  // Financial & Scope
  financialSummary: assetDashboardFinancialSummarySchema.nullable(),
  scopeInfo: assetDashboardScopeInfoSchema,
  generatedAt: instantSchema,
});
export type AssetDashboard = z.infer<typeof assetDashboardSchema>;

// ---------------------------------------------------------------------------
// Query Schemas
// ---------------------------------------------------------------------------

export const listAssetsQuerySchema = paginationQuerySchema.extend({
  categoryId: uuidSchema.optional(),
  typeId: uuidSchema.optional(),
  status: assetStatusSchema.optional(),
  locationId: uuidSchema.optional(),
  personId: uuidSchema.optional(),
  search: z.string().trim().optional(),
  /** Filter on one custom field's value (AST-12): text = contains, others = exact. */
  customFieldId: uuidSchema.optional(),
  customFieldValue: z.string().trim().min(1).max(200).optional(),
  /** Named preset: `warranty_expiring` = warranty ends within the next 30 days (the dashboard alert). */
  filter: z.enum(['warranty_expiring']).optional(),
});
export type ListAssetsQuery = z.infer<typeof listAssetsQuerySchema>;

export const listAssetRequestsQuerySchema = paginationQuerySchema.extend({
  status: assetRequestStatusSchema.optional(),
  requestType: assetRequestTypeSchema.optional(),
  categoryId: uuidSchema.optional(),
  personId: uuidSchema.optional(),
  search: z.string().trim().optional(),
});
export type ListAssetRequestsQuery = z.infer<typeof listAssetRequestsQuerySchema>;

export const listAllocationsQuerySchema = paginationQuerySchema.extend({
  personId: uuidSchema.optional(),
  assetId: uuidSchema.optional(),
  open: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
  unacknowledged: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
  returnRequested: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});
export type ListAllocationsQuery = z.infer<typeof listAllocationsQuerySchema>;

export const listMaintenanceIssuesQuerySchema = paginationQuerySchema.extend({
  assetId: uuidSchema.optional(),
  reporterId: uuidSchema.optional(),
  technicianId: uuidSchema.optional(),
  priority: maintenanceIssuePrioritySchema.optional(),
  status: maintenanceIssueStatusSchema.optional(),
  search: z.string().trim().optional(),
});
export type ListMaintenanceIssuesQuery = z.infer<typeof listMaintenanceIssuesQuerySchema>;

// ---------------------------------------------------------------------------
// Contract Router
// ---------------------------------------------------------------------------

export const assetContract = c.router(
  {
    // Dashboard & Analytics
    getAssetDashboard: {
      method: 'GET',
      path: '/dashboard',
      summary:
        'Get real-time aggregated operational metrics for Asset Management (asset:analytics:read or asset:read)',
      metadata: routeMeta(['asset:analytics:read', 'asset:read']),
      responses: {
        200: assetDashboardSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },

    // Categories & Types
    listCategories: {
      method: 'GET',
      path: '/categories',
      summary: 'List active asset categories',
      metadata: routeMeta(null),
      responses: {
        200: z.array(assetCategorySchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },

    createCategory: {
      method: 'POST',
      path: '/categories',
      summary: 'Create an asset category (asset.category:manage)',
      headers: idempotencyHeadersSchema,
      body: createAssetCategoryBodySchema,
      metadata: routeMeta('asset.category:manage'),
      responses: {
        201: assetCategorySchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
    },

    listTypes: {
      method: 'GET',
      path: '/types',
      summary: 'List active asset types, optionally filtered by category',
      query: z.object({ categoryId: uuidSchema.optional() }),
      metadata: routeMeta(null),
      responses: {
        200: z.array(assetTypeSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },

    createType: {
      method: 'POST',
      path: '/types',
      summary: 'Create an asset type under a category (asset.category:manage)',
      headers: idempotencyHeadersSchema,
      body: createAssetTypeBodySchema,
      metadata: routeMeta('asset.category:manage'),
      responses: {
        201: assetTypeSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
    },

    // Assets Inventory & Detail
    listAssets: {
      method: 'GET',
      path: '/assets',
      summary: 'List assets with pagination and filters (asset:read)',
      query: listAssetsQuerySchema,
      metadata: routeMeta(null),
      responses: {
        200: paginatedSchema(assetSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },

    getAsset: {
      method: 'GET',
      path: '/assets/:assetId',
      summary: 'Get full asset details by ID (asset:read)',
      pathParams: z.object({ assetId: uuidSchema }),
      metadata: routeMeta('asset:read'),
      responses: {
        200: assetSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },

    getAssetHistory: {
      method: 'GET',
      path: '/assets/:assetId/history',
      summary: 'Get structured timeline events for an asset (asset:read)',
      pathParams: z.object({ assetId: uuidSchema }),
      // The current custodian may read their own asset's history without `asset:read`; the handler
      // checks custodian-or-`asset:read` against the asset.
      metadata: routeMeta(null),
      responses: {
        200: z.array(assetHistoryEventSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },

    createAsset: {
      method: 'POST',
      path: '/assets',
      summary: 'Create a new asset in the register (asset:create)',
      headers: idempotencyHeadersSchema,
      body: createAssetBodySchema,
      metadata: routeMeta('asset:create'),
      responses: {
        201: assetSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
    },

    patchAsset: {
      method: 'PATCH',
      path: '/assets/:assetId',
      summary: 'Update descriptive asset fields (asset:edit)',
      pathParams: z.object({ assetId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: patchAssetBodySchema,
      metadata: routeMeta('asset:edit'),
      responses: {
        200: assetSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
    },

    getMyAssets: {
      method: 'GET',
      path: '/me/assets',
      summary: 'List current employee open asset assignments (asset:read)',
      metadata: routeMeta(null),
      responses: {
        200: z.array(assetAssignmentSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },

    // Custody Lifecycle Operations
    assignAsset: {
      method: 'POST',
      path: '/assets/:assetId/assign',
      summary: 'Directly assign an available asset to a custodian (asset:assign)',
      pathParams: z.object({ assetId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: assignAssetBodySchema,
      metadata: routeMeta('asset:assign'),
      responses: {
        200: assetAssignmentSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    transferAsset: {
      method: 'POST',
      path: '/assets/:assetId/transfer',
      summary: 'Transfer an assigned asset to a new custodian (asset:transfer)',
      pathParams: z.object({ assetId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: transferAssetBodySchema,
      metadata: routeMeta('asset:transfer'),
      responses: {
        200: assetAssignmentSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    returnAsset: {
      method: 'POST',
      path: '/assets/:assetId/return',
      summary: 'Process return/check-in of an assigned asset (asset:assign)',
      pathParams: z.object({ assetId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: returnAssetBodySchema,
      metadata: routeMeta(['asset:assign', 'asset:return']),
      responses: {
        200: assetSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    // Lifecycle: retire / dispose / lost / damaged (AST-7)
    retireAsset: {
      method: 'POST',
      path: '/assets/:assetId/retire',
      summary: 'Retire an available or damaged asset from service (asset:retire)',
      pathParams: z.object({ assetId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: retireAssetBodySchema,
      metadata: routeMeta('asset:retire'),
      responses: {
        200: assetSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    disposeAsset: {
      method: 'POST',
      path: '/assets/:assetId/dispose',
      summary:
        'Dispose of a retired or lost asset: method, date and value received (asset:dispose)',
      pathParams: z.object({ assetId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: disposeAssetBodySchema,
      metadata: routeMeta('asset:dispose'),
      responses: {
        200: assetSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
    },

    markAssetLost: {
      method: 'POST',
      path: '/assets/:assetId/mark-lost',
      summary: 'Mark an asset lost, closing any open custody (asset:retire)',
      pathParams: z.object({ assetId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: markAssetLostBodySchema,
      metadata: routeMeta('asset:retire'),
      responses: {
        200: assetSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    markAssetDamaged: {
      method: 'POST',
      path: '/assets/:assetId/mark-damaged',
      summary: 'Mark an unassigned asset damaged and take it out of circulation (asset:retire)',
      pathParams: z.object({ assetId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: markAssetDamagedBodySchema,
      metadata: routeMeta('asset:retire'),
      responses: {
        200: assetSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    // Requests & Approvals
    listRequests: {
      method: 'GET',
      path: '/requests',
      summary: 'List asset requests with filters and pagination (asset.request:create)',
      query: listAssetRequestsQuerySchema,
      metadata: routeMeta(null),
      responses: {
        200: paginatedSchema(assetRequestSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },

    listApprovalsInbox: {
      method: 'GET',
      path: '/requests/approvals',
      summary: 'List asset requests awaiting approval by the caller (asset.request:create)',
      metadata: routeMeta(null),
      responses: {
        200: z.array(assetRequestSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },

    getRequest: {
      method: 'GET',
      path: '/requests/:requestId',
      summary: 'Get details of an asset request (asset.request:create)',
      pathParams: z.object({ requestId: uuidSchema }),
      metadata: routeMeta(['asset.request:create', 'asset.request:approve']),
      responses: {
        200: assetRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },

    createRequest: {
      method: 'POST',
      path: '/requests',
      summary: 'Submit an asset request (asset.request:create)',
      headers: idempotencyHeadersSchema,
      body: createAssetRequestBodySchema,
      metadata: routeMeta('asset.request:create'),
      responses: {
        201: assetRequestSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
    },

    approveRequest: {
      method: 'POST',
      path: '/requests/:requestId/approve',
      summary: 'Approve an asset request at the current workflow step (asset.request:create)',
      pathParams: z.object({ requestId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: decideAssetRequestBodySchema.optional(),
      metadata: routeMeta(null),
      responses: {
        200: assetRequestSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    rejectRequest: {
      method: 'POST',
      path: '/requests/:requestId/reject',
      summary: 'Reject an asset request (asset.request:create)',
      pathParams: z.object({ requestId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: rejectAssetRequestBodySchema,
      metadata: routeMeta(null),
      responses: {
        200: assetRequestSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    cancelRequest: {
      method: 'POST',
      path: '/requests/:requestId/cancel',
      summary: 'Cancel a pending or approved asset request (asset.request:create)',
      pathParams: z.object({ requestId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: z.object({}).optional(),
      metadata: routeMeta(null),
      responses: {
        200: assetRequestSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    allocateRequest: {
      method: 'POST',
      path: '/requests/:requestId/allocate',
      summary: 'Allocate an available asset to fulfill an approved request (asset:assign)',
      pathParams: z.object({ requestId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: allocateAssetRequestBodySchema,
      metadata: routeMeta('asset:assign'),
      responses: {
        200: assetAssignmentSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
    },

    // Custodian Actions
    acknowledgeAssignment: {
      method: 'POST',
      path: '/assignments/:assignmentId/acknowledge',
      summary: 'Custodian digital acceptance for an assigned asset (asset:read)',
      pathParams: z.object({ assignmentId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: acknowledgeAssignmentWithExceptionBodySchema.optional(),
      metadata: routeMeta(null),
      responses: {
        200: assetAssignmentSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    returnRequestAssignment: {
      method: 'POST',
      path: '/assignments/:assignmentId/return-request',
      summary: 'Custodian request to initiate asset return (asset:read)',
      pathParams: z.object({ assignmentId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: returnRequestBodySchema.optional(),
      metadata: routeMeta(null),
      responses: {
        200: assetAssignmentSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    // Allocations List
    listAllocations: {
      method: 'GET',
      path: '/allocations',
      summary: 'List asset assignments and custody records with filters (asset:read)',
      query: listAllocationsQuerySchema,
      metadata: routeMeta(null),
      responses: {
        200: paginatedSchema(assetAssignmentSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },

    // Maintenance & Issues
    listMaintenanceIssues: {
      method: 'GET',
      path: '/maintenance',
      summary:
        'List maintenance issues with filters and pagination (asset:maintenance:read or asset:read)',
      query: listMaintenanceIssuesQuerySchema,
      // Row-scoped: every caller sees the issues they reported or are the technician on; grants
      // widen the scope. No permission is required to reach it.
      metadata: routeMeta(null),
      responses: {
        200: paginatedSchema(maintenanceIssueSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },

    getMaintenanceIssue: {
      method: 'GET',
      path: '/maintenance/:issueId',
      summary:
        'Get maintenance issue detail with timeline and comments (asset:maintenance:read or asset:read)',
      pathParams: z.object({ issueId: uuidSchema }),
      // The reporter, the assigned technician and the reporter's managers may read with no asset
      // permission; the handler checks.
      metadata: routeMeta(null),
      responses: {
        200: maintenanceIssueSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },

    createMaintenanceIssue: {
      method: 'POST',
      path: '/maintenance',
      summary: 'Report a new hardware issue or maintenance request (asset:maintenance:create)',
      headers: idempotencyHeadersSchema,
      body: createMaintenanceIssueBodySchema,
      // The custodian and their managers may report an issue with no asset permission; the handler
      // checks.
      metadata: routeMeta(null),
      responses: {
        201: maintenanceIssueSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
    },

    patchMaintenanceIssue: {
      method: 'PATCH',
      path: '/maintenance/:issueId',
      summary: 'Update issue status, diagnostics, parts, or resolution (asset:maintenance:manage)',
      pathParams: z.object({ issueId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: patchMaintenanceIssueBodySchema,
      // The assigned technician may update their ticket with no asset permission; the handler checks
      // technician-or-`asset:maintenance:manage`. Changing the technician needs the permission.
      metadata: routeMeta(null),
      responses: {
        200: maintenanceIssueSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    assignTechnician: {
      method: 'POST',
      path: '/maintenance/:issueId/assign-technician',
      summary: 'Assign or reassign an IT technician with SLA target (asset:maintenance:manage)',
      pathParams: z.object({ issueId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: assignTechnicianBodySchema,
      metadata: routeMeta('asset:maintenance:manage'),
      responses: {
        200: maintenanceIssueSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    issueLoanerAsset: {
      method: 'POST',
      path: '/maintenance/:issueId/loaner',
      summary: 'Issue a temporary loaner device swap for maintenance ticket (asset:assign)',
      pathParams: z.object({ issueId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: issueLoanerAssetBodySchema,
      metadata: routeMeta(['asset:assign', 'asset:maintenance:manage']),
      responses: {
        200: maintenanceIssueSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },

    addMaintenanceComment: {
      method: 'POST',
      path: '/maintenance/:issueId/comments',
      summary:
        'Add a diagnostic update or discussion comment to an issue (asset:maintenance:read or asset:read)',
      pathParams: z.object({ issueId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: createMaintenanceCommentBodySchema,
      // The reporter and the assigned technician may comment with no asset permission; the handler
      // checks.
      metadata: routeMeta(null),
      responses: {
        201: maintenanceCommentSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
  },
  {
    pathPrefix: '/asset',
  },
);
